/* eslint-disable max-len */
import {
  SimpleForm,
  required,
  ReferenceInput,
  AutocompleteInput,
  useTranslate,
  TextInput
} from "react-admin";
import { useLocation } from "react-router-dom";
import * as User from "../providers/user";
import { Dispatch, SetStateAction, useState } from "react";
import { GET_ARTISTS, GET_LOCATIONS } from "../gql";
import { useQuery } from "@apollo/client";
import { CustomButton } from "./CustomButton";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import "yup-phone";
import { FailedOrderIcon } from "assets/Icons";
import dayjs from "dayjs";
import * as Axios from "providers/axios";
import TimePickerInput from "./TimePickerInput";
import DatePickerInput from "./DatePickerInput";

interface BlockTimeFromArtistCalendarProps {
  artist: any;
  selectedDate?: string;
  selectedStart?: string;
  selectedEnd?: string;
  closeCreateDialogAndRefresh?: () => void;
  createDialogOpen: string | null;
  filteredLocationId?: string;
  closeCreateDialog: () => void;
  setBlockTimeDialogOpen: Dispatch<SetStateAction<boolean>>;
}

export interface LocationResult {
  id: string;
  company_id: string;
  timezone: string;
}

interface RequestBody {
  start_time: string;
  end_time: string;
  location_id: string;
  artist_id: string;
  notes?: string;
}

export const emptyBookAppointmentFull = {
  start_time: "",
  end_time: "",
  location: null,
  services: [],
  payment_method_id: "",
  bookingUnavailable: true
};

export default function BlockTimeFromArtistCalendar({
  artist,
  selectedStart,
  selectedEnd,
  closeCreateDialogAndRefresh,
  filteredLocationId,
  createDialogOpen,
  closeCreateDialog,
  setBlockTimeDialogOpen
}: BlockTimeFromArtistCalendarProps) {
  const { permissions } = User.useUser();
  const { api } = Axios.useAxios();
  const translate = useTranslate();
  const location = useLocation();
  const [errors, setErrors] = useState<any>({});
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const isCustomErrors = Object.keys(errors)?.length > 0;

  const artistsResult = useQuery(GET_ARTISTS, {
    variables: {
      where: {
        user: {
          is: {
            joined_company_id: { equals: permissions?.companyId }
          }
        },
        is_active: {
          equals: true
        }
      }
    },
    fetchPolicy: "no-cache"
  });
  const data = artistsResult?.data ?? artistsResult?.previousData;

  const locationsResult = useQuery(GET_LOCATIONS, {
    variables: {
      where: {
        company_id: { equals: permissions?.companyId }
      }
    },
    fetchPolicy: "no-cache"
  });
  const locationsData: LocationResult[] =
    (locationsResult?.data ?? locationsResult?.previousData)?.locations ?? [];

  const createValidationSchema = () => {
    const baseSchema = {
      date: yup.date().nullable().required("Date required."),
      start: yup.date().nullable().required("Start time required."),
      end: yup.date().nullable().required("End time required."),
      location: yup.object({
        id: yup.string().nullable().required("Location selection required.")
      }),
      artist: yup.object({
        id: yup.string().nullable().required("Artist selection required.")
      })
    };

    return yup.object().shape(baseSchema).required();
  };

  const getTimeOfDayInMilliseconds = (date: Date) => {
    const hours = date.getHours() * 60 * 60 * 1000;
    const minutes = date.getMinutes() * 60 * 1000;
    return hours + minutes;
  };

  const submit = async (submitData: any) => {
    let customErrors: any = {};

    const startMill = getTimeOfDayInMilliseconds(submitData.start);
    const endMill = getTimeOfDayInMilliseconds(submitData.end);
    if (startMill && endMill && startMill >= endMill) {
      customErrors.end = "End time must be after start time";
    }
    if (Object.keys(customErrors)?.length > 0) {
      setErrors(customErrors);
      return;
    }
    try {
      setIsSaving(true);
      const {
        date: submitDate,
        start,
        end,
        location,
        artist,
        notes
      } = submitData;
      const locationTimezone =
        locationsData.find((loc) => loc?.id === location?.id)?.timezone ??
        "PST8PDT";

      const mdate = dayjs.tz(submitDate as string, locationTimezone);

      const padZero = (num: any) => num.toString().padStart(2, "0");

      const year = mdate.year();
      const month = padZero(mdate.month() + 1);
      const day = padZero(dayjs(submitDate as string).date());
      const startHour = padZero(start.getHours());
      const startMinute = padZero(start.getMinutes());
      const endHour = padZero(end.getHours() === 0 ? 24 : end.getHours());
      const endMinute = padZero(end.getMinutes());

      const startDateTimeString = `${year}-${month}-${day} ${startHour}:${startMinute}`;
      const endDateTimeString = `${year}-${month}-${day} ${endHour}:${endMinute}`;

      const start_time = dayjs.tz(
        startDateTimeString,
        "YYYY-MM-DD HH:mm",
        locationTimezone
      );
      const end_time = dayjs.tz(
        endDateTimeString,
        "YYYY-MM-DD HH:mm",
        locationTimezone
      );
      const requestBody: RequestBody = {
        start_time: start_time.format(),
        end_time: end_time.format(),
        location_id: location?.id,
        artist_id: artist?.id,
        notes: notes
      };
      await api.post(`/v1/artists/block-time`, requestBody);
      setIsSaving(false);
      closeCreateDialogAndRefresh?.();
    } catch (error) {
      setIsSaving(false);
    }
  };

  const disabled =
    permissions.role.role !== "OWNER" &&
    permissions.role.role !== "MANAGER" &&
    !permissions.isSuperAdmin;

  return (
    <Dialog
      key={artist?.id}
      open={createDialogOpen === artist?.id}
      onClose={(_, reason: any) => {
        if (reason === "backdropClick") {
          return;
        }
        closeCreateDialog();
      }}
      sx={{
        "& .MuiDialogContent-root": {
          padding: "0px"
        },
        "& .MuiDialog-paper": {
          borderRadius: "8px",
          backgroundColor: "#fff",
          maxWidth: "480px",
          width: "100%",
          "@media screen and (max-width:926px)": {
            maxWidth: "480px"
          }
        }
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          padding: "4px 16px 0px 16px"
        }}
      >
        <div className="w-full">
          <div className="w-full flex justify-between">
            <div className="w-full flex justify-center items-center -mr-[42px] text-[14px] font-['Inter'] font-[600] uppercase">
              Block Time
            </div>
            <IconButton onClick={closeCreateDialog}>
              <CloseIcon />
            </IconButton>
          </div>
          <div className="w-full flex justify-center gap-3 mb-3">
            <CustomButton
              variant="secondary"
              type="button"
              text={translate("appointment.create_pointment")}
              onClick={() => setBlockTimeDialogOpen(false)}
            />
            <CustomButton variant="primary" type="button" text="Block Time" />
          </div>
        </div>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            backgroundColor: "#fff",
            "& .RaToolbar-desktopToolbar": {
              backgroundColor: "#fff",
              minHeight: "40px",
              padding: "0px 16px 16px 16px"
            },
            "& .RaToolbar-mobileToolbar": {
              position: "relative !important",
              backgroundColor: "#fff",
              minHeight: "40px",
              padding: "0px 16px 16px 16px !important"
              // zIndex: 90
            },
            "& .RaCreate-noActions": {
              marginTop: 0
            }
          }}
        >
          <SimpleForm
            onSubmit={submit}
            // validate={validateDates}
            resolver={yupResolver(createValidationSchema())}
            className="create-form"
            id="save-create-appointment"
            sx={{
              // backgroundColor: "#fff",
              padding: "0px 16px !important",
              "& .MuiFilledInput-root": {
                borderRadius: "8px",
                border: "1px solid rgba(48, 47, 46, 0.06)"
              },
              "& .MuiFilledInput-input": {
                height: "24px",
                fontSize: "16px",
                borderRadius: "8px",
                fontFamily: "Inter",
                border: "none"
              },
              "& .MuiFilledInput-input:focus": {
                boxShadow: "none",
                borderRadius: "8px !important"
              },
              "& .MuiFilledInput-root:before": {
                border: "none !important"
              },
              "& .MuiFilledInput-root:after": {
                border: "1px solid #b8722a !important",
                top: "0px",
                borderRadius: "8px",
                transition: "none"
              },
              "& .MuiFilledInput-root.Mui-error:after": {
                border: "1px solid #EF5B1B !important"
              },
              "& .MuiFilledInput-root.Mui-disabled": {
                backgroundColor: "rgba(48, 47, 46, 0.06)"
              },
              "& .MuiTextField-root": {
                marginTop: "3px",
                marginBottom: "3px"
              },
              "& .MuiInputLabel-root": {
                fontSize: "16px",
                color: "rgba(48, 47, 46, 0.6)"
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#b8722a"
              },
              "& .MuiFormHelperText-root": {
                fontSize: "12px",
                marginTop: "2px",
                marginBottom: "2px",
                lineHeight: "14px"
              }
            }}
            toolbar={false}
          >
            <div className="w-full">
              <div className="w-full h-[2px] mt-2 mb-4 bg-[rgba(0,0,0,0.07)]" />
              <h5 className="mb-2">
                {translate("appointment.general_details")}:
              </h5>
              <DatePickerInput
                source="date"
                disabled={disabled}
                label={translate("availability.date")}
                defaultValue={
                  selectedStart
                    ? new Date(selectedStart)
                    : location.state?.range?.start_time
                }
                validate={[required()]}
                fullWidth
              />
              <TimePickerInput
                source="start"
                disabled={disabled}
                label={translate("availability.start")}
                defaultValue={
                  selectedStart && !!selectedEnd
                    ? new Date(selectedStart)
                    : location.state?.range?.start_time
                }
                validate={[required()]}
                fullWidth
              />
              <TimePickerInput
                source="end"
                disabled={disabled}
                label={translate("availability.end")}
                defaultValue={
                  selectedEnd
                    ? new Date(selectedEnd)
                    : location.state?.range?.end_time
                }
                validate={[required()]}
                fullWidth
              />
              {locationsData && locationsData.length > 0 && (
                <ReferenceInput
                  source="location.id"
                  reference="Location"
                  filter={{ company_id: permissions.companyId }}
                >
                  <AutocompleteInput
                    optionText={(value) => value.nickname || value.name}
                    disabled={disabled}
                    label={translate("availability.location")}
                    fullWidth
                    defaultValue={
                      filteredLocationId === "all"
                        ? undefined
                        : filteredLocationId
                    }
                    validate={[required()]}
                  />
                </ReferenceInput>
              )}
              {data?.artists && data?.artists?.length > 0 && (
                <AutocompleteInput
                  source="artist.id"
                  choices={
                    data?.artists?.map((item: any) => ({
                      id: item?.id,
                      name: `${item?.user?.first_name} ${item?.user?.last_name}`
                    })) || []
                  }
                  optionText={(value) => value?.name}
                  disabled={disabled}
                  defaultValue={artist?.id}
                  label={translate("appointmentservices.artist")}
                  fullWidth
                  validate={[required()]}
                />
              )}
              <TextInput
                source="notes"
                label={translate("appointment.note")}
                fullWidth
                multiline
              />
            </div>
          </SimpleForm>
          {isCustomErrors && (
            <Dialog
              open={isCustomErrors}
              onClose={() => setErrors({})}
              sx={{
                "& .MuiDialogContent-root": {
                  padding: "0px"
                },
                "& .MuiDialog-paper": {
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                  maxWidth: "520px",
                  width: "100%"
                }
              }}
            >
              <DialogTitle
                sx={{ display: "flex", padding: "4px 16px 0px 16px" }}
              >
                <div className="w-full flex justify-center items-center ml-[-12px] text-[14px] font-['Inter'] font-[600]" />
                <IconButton onClick={() => setErrors({})}>
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent>
                <div className="w-full flex flex-col justify-center items-center px-2">
                  <FailedOrderIcon />

                  <p className="text-[24px] font-['OptimaLTStd'] font-[500] text-mono-primaryTextColor mt-4">
                    {translate("appointment.you_need_to_fill_required_fields")}
                  </p>
                  <div className="my-6">
                    {Object.values(errors).map((error: any, index: number) => (
                      <p
                        key={index}
                        className="text-[18px] font-['OptimaLTStd'] font-[500] text-mono-primaryTextColor mt-1"
                      >
                        {index + 1}. {error}
                      </p>
                    ))}
                  </div>
                </div>
              </DialogContent>
              <DialogActions>
                <div className="w-full flex justify-center mb-4">
                  <CustomButton
                    variant="primary"
                    text={translate("appointment.got_it")}
                    onClick={() => setErrors({})}
                  />
                </div>
              </DialogActions>
            </Dialog>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ paddingX: "16px" }}>
        <div className="w-full flex justify-end items-center">
          <CustomButton
            type="submit"
            text={translate("common.save")}
            form="save-create-appointment"
            submitting={isSaving}
            disabled={isSaving || disabled}
          />
        </div>
      </DialogActions>
    </Dialog>
  );
}
